import { type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'

/**
 * Generic confirmation dialog used by the CRUD pages.
 *
 * Three behaviours folded in here:
 *   - **Delete-style** confirmations (destructive, red button).
 *   - **Deactivate-style** confirmations (warning, amber/orange).
 *   - Anything else benign (default neutral primary).
 *
 * The CRUD pages (Clients, Vehicles, Drivers) all need a "Are
 * you sure?" prompt with the same shape — a title, a sentence
 * naming the row, an optional error message, and two buttons.
 * Re-implementing it three times would drift on visual cues
 * almost immediately, so we centralise once.
 *
 * Note: the Planning page already has its own inlined confirm
 * (passe 1 admin). It works fine, so we don't refactor for
 * refactor's sake — if a fifth caller appears, we migrate
 * Planning to this component too.
 */
export type ConfirmTone = 'danger' | 'warning' | 'primary'

interface ConfirmDialogProps {
  /** Whether the dialog is mounted/visible. */
  open: boolean
  onOpenChange: (open: boolean) => void

  title: string
  description?: string
  /** Optional rich preview of the affected row (renders above the buttons). */
  children?: ReactNode

  confirmLabel: string
  /** "Annuler" by default. */
  cancelLabel?: string
  tone?: ConfirmTone

  onConfirm: () => void
  /** Disables the confirm button + shows a spinner. */
  loading?: boolean
  /** Surface a server-side error inline rather than via a toast. */
  errorMessage?: string | null
}

const TONE_TO_VARIANT: Record<ConfirmTone, 'danger' | 'primary'> = {
  // Warning maps to `primary` for the button — there's no
  // "amber" button variant and we don't need one. The dialog
  // header carries the tone; the action button stays
  // brand-coloured.
  danger: 'danger',
  warning: 'primary',
  primary: 'primary',
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel,
  cancelLabel = 'Annuler',
  tone = 'primary',
  onConfirm,
  loading = false,
  errorMessage,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description !== undefined ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>
        {children !== undefined || errorMessage ? (
          <DialogBody>
            {children}
            {errorMessage !== null && errorMessage !== undefined && errorMessage !== '' ? (
              <p className="mt-3 text-sm text-danger">{errorMessage}</p>
            ) : null}
          </DialogBody>
        ) : null}
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="ghost" disabled={loading}>
              {cancelLabel}
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant={TONE_TO_VARIANT[tone]}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? <Spinner label={confirmLabel} /> : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
